import { datadogLogs } from '@datadog/browser-logs';
import { LoggerFactory, LogLevel } from '@remento/logger';
import { BrowserConsoleLogTransport } from '@remento/logger/transport/console.browser';
import { parseLogLevel } from '@remento/logger/types';
import { BrowserDatadogLogTransport } from '@remento/logger-datadog/datadog.browser';
import { BrowserAnalytics } from '@remento/web-analytics-client/analytics.browser';
import { AnalyticsServiceClient } from '@remento/web-analytics-client/analytics-service-client';
import { IDRepository } from '@remento/web-analytics-client/id-repository';
import { IDServiceClient } from '@remento/web-analytics-client/id-service-client';
import { Debugger } from '@remento/web-analytics-router/debugger';
import { Router } from '@remento/web-analytics-router/router';
import { TermlyConsentRepository } from '@remento/web-consent-client/consent-repository-termly';

import { BrowserAnalyticsReplayConfig, InitializeCallback } from './@types.js';
import { initializeReplay } from './init-replay.js';

export async function initializeHost(callback: InitializeCallback) {
  const url = new URL(document.URL);
  const datadogConfig = {
    clientToken: process.env.RO_ANALYTICS_DD_APP_TOKEN!,
    trackingConsent: 'not-granted' as const,
    env: process.env.RO_ENV == 'prod' ? 'production' : 'development',
    proxy: (options: any) =>
      `https://${process.env.RO_ANALYTICS_DD_API_HOST}${process.env.RO_ANALYTICS_DD_API_PATH}` +
      `${options.path}?${options.parameters}`,
    service: process.env.RO_ANALYTICS_DD_SERVICE,
    site: process.env.RO_ANALYTICS_DD_SITE!,
  };
  datadogLogs.init({
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    ...datadogConfig,
  });

  const rootLogger = new LoggerFactory({
    transports: [new BrowserConsoleLogTransport(), new BrowserDatadogLogTransport(datadogLogs.logger)],
    metadata: {},
    defaultLevel: parseLogLevel(url.searchParams.get('ro_logger_level') ?? null) ?? LogLevel.warn,
  });
  const logger = rootLogger.createLogger('analytics');

  if (window.ANALYTICS_PAGE == null) {
    logger.warn('MISSING_CONFIG', () => ({ key: 'ANALYTICS_PAGE' }));
  }

  const idRepository = new IDRepository(process.env.RO_ANALYTICS_ROOT_DOMAIN!);
  const consentRepository = new TermlyConsentRepository(logger.extend('consent-client'));
  const idService = new IDServiceClient(`${process.env.RO_ANALYTICS_API_URL!}`);
  const analyticsService = new AnalyticsServiceClient(`${process.env.RO_ANALYTICS_API_URL!}`);
  const analyticsRouter = new Router(logger.extend('router'));

  const user = idRepository.get();
  if (user.source != 'cookies' && user.source != 'session') {
    idRepository.save(user);
  }
  const analyticsConfig = {
    source: process.env.RO_ANALYTICS_SOURCE!,
    segment: {
      key: process.env.RO_ANALYTICS_SEGMENT_KEY!,
      cdnURL: `https://${process.env.RO_ANALYTICS_SEGMENT_CDN_HOST!}${process.env.RO_ANALYTICS_SEGMENT_CDN_PATH!}`,
      apiHost: `${process.env.RO_ANALYTICS_SEGMENT_API_HOST!}${process.env.RO_ANALYTICS_SEGMENT_API_PATH!}`,
    },
  };
  const analyticsClient = new BrowserAnalytics(
    analyticsService,
    idService,
    logger.extend('client'),
    user,
    analyticsConfig,
  );
  const debug =
    url.searchParams.get('ro_debug') == null
      ? null
      : new Debugger(
          `${analyticsConfig.source}.${window.ANALYTICS_PAGE}`,
          { bootstrap: { namespace: 'tw-bt' } },
          analyticsRouter,
          logger.extend('debugger'),
          analyticsClient,
        );

  analyticsClient.on('identify', (user) => {
    idRepository.save(user);
    datadogLogs.setUser({ id: user.id });
  });
  analyticsClient.on('reset', (user) => {
    idRepository.save(user);
    datadogLogs.clearUser();
  });
  consentRepository.on('change', (consent) => {
    datadogLogs.setTrackingConsent(consent.analytics == true ? 'granted' : 'not-granted');
  });

  // Let callback register transforms, handlers, and observers on the router
  await callback(analyticsRouter, analyticsClient, debug, logger);
  for (const cb of window.ANALYTICS_INITIALIZERS ?? []) {
    await cb(analyticsRouter, analyticsClient, debug, logger);
  }
  await debug?.init();

  // Load the consent. This may take a while (or fail) since it relies on Termly which is loaded
  // externally.
  consentRepository.getConsentState().then((consent) => {
    datadogLogs.setTrackingConsent(consent.analytics ? 'granted' : 'not-granted');
  });

  // Load datadog. Some VPNs and other privacy extensions will block it, or we may
  // determine the session is not worth tracking. It's not worth waiting for it
  const sessionReplayConfig: BrowserAnalyticsReplayConfig = {
    applicationId: process.env.RO_ANALYTICS_DD_APP_ID!,
    defaultPrivacyLevel: 'mask-user-input' as const,
    sessionSampleRate: parseInt(process.env.RO_ANALYTICS_DD_SAMPLE_RATE!),
    sessionReplaySampleRate: parseInt(process.env.RO_ANALYTICS_DD_SAMPLE_RATE!),
    trackUserInteractions: true,
    ...datadogConfig,
  };
  initializeReplay(logger, user.id, sessionReplayConfig)
    .then(async (datadogRum) => {
      if (datadogRum == null) {
        return;
      }
      // Update the consent status since we always initialize without it
      const consent = await consentRepository.getConsentState();
      datadogRum.setTrackingConsent(consent.analytics == true ? 'granted' : 'not-granted');

      consentRepository.on('change', (consent) => {
        datadogRum.setTrackingConsent(consent.analytics == true ? 'granted' : 'not-granted');
      });
      analyticsClient.on('identify', (user) => {
        datadogRum.setUser({ id: user.id });
      });
      analyticsClient.on('reset', (user) => {
        datadogRum.clearUser();
      });
      analyticsClient.on('track', (event, properties) => {
        datadogRum.addAction(event, properties);
      });
      analyticsClient.on('page', (page) => {
        datadogRum.startView(page);
      });
    })
    .catch((err) => logger.warn('DD_INIT_ERROR', () => ({ error: err })));

  logger.info('INIT');
}
